import { ORDER_DATE_CONFIGURATION_KEY } from '@/utils/orderDate';
import { PERSONALIZATION_NOTES_CONFIGURATION_KEY } from '@/utils/personalizationNotes';
import type { ProductDefinition } from '@/types/productDefinition';
import {
	BAG_CUSTOM_MATERIAL_COLOR_ATTRIBUTE_ID,
	CARTON_CUSTOM_EXTERNAL_COLOR_ATTRIBUTE_ID,
	CARTON_CUSTOM_INTERNAL_COLOR_ATTRIBUTE_ID,
	isCustomColorConfigurationKey,
} from '@/utils/customColors';
import { TAPE_CUSTOM_MATERIAL_COLOR_ATTRIBUTE_ID } from '@/utils/tapeAplaColor';

const isUuid = (value: string) =>
	/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i.test(value);

/** Functional keys that must survive preferUuidConfiguration (not product attributes). */
const PRESERVED_CONFIGURATION_KEYS = new Set([
	ORDER_DATE_CONFIGURATION_KEY,
	PERSONALIZATION_NOTES_CONFIGURATION_KEY,
	'quantity',
	'cartons.quantity',
	'sizeMode',
	'size',
	'format',
	'customWidth',
	'customHeight',
	'customDepth',
	// Envelope / bag print module (product-definition functional ids)
	'printAreaFront',
	'printColorsFront',
	'printSizeFront',
	'finishFront',
	'printAreaBack',
	'printColorsBack',
	'printSizeBack',
	'finishBack',
	// Carton / cardboard-envelope print module
	'print.external.area',
	'print.external.colors',
	'print.external.size',
	'print.external.finish',
	'print.internal.area',
	'print.internal.colors',
	'print.internal.size',
	'print.internal.finish',
	CARTON_CUSTOM_EXTERNAL_COLOR_ATTRIBUTE_ID,
	CARTON_CUSTOM_INTERNAL_COLOR_ATTRIBUTE_ID,
	BAG_CUSTOM_MATERIAL_COLOR_ATTRIBUTE_ID,
	TAPE_CUSTOM_MATERIAL_COLOR_ATTRIBUTE_ID,
]);

const isPreservedConfigurationKey = (key: string) => {
	if (PRESERVED_CONFIGURATION_KEYS.has(key)) return true;
	// Print-module / tape functional ids from product definitions.
	if (
		key.startsWith('print.') ||
		key.startsWith('tape.') ||
		key.startsWith('bag.') ||
		key.startsWith('envelope.') ||
		key.startsWith('cartons.') ||
		key.startsWith('printArea') ||
		key.startsWith('printColors') ||
		key.startsWith('printSize') ||
		key === 'finishFront' ||
		key === 'finishBack' ||
		key === 'materialColor' ||
		key === 'materialType' ||
		isCustomColorConfigurationKey(key)
	) {
		return true;
	}
	return false;
};

const normalizeKey = (key: string) => {
	const slug = key
		.toLowerCase()
		.normalize('NFD')
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/(^-|-$)/g, '');

	switch (slug) {
		case 'liczba-kartonow':
		case 'liczba-kartono-w':
		case 'naklad':
		case 'ilosc-kartonow':
			return 'quantity';
		default:
			return key;
	}
};

/**
 * Removes duplicate configuration entries, preferring human‑readable/non‑UUID keys.
 * Keeps a normalized "quantity" alias and drops UUID keys when a non‑UUID alias exists.
 */
export const canonicalizeConfiguration = (
	configuration: Record<string, string | number | null | undefined>
): Record<string, string | number | null> => {
	const result: Record<string, string | number | null> = {};

	// First pass – keep first non‑UUID occurrence for a key
	Object.entries(configuration ?? {}).forEach(([rawKey, value]) => {
		const normalizedKey = normalizeKey(rawKey);
		const uuidKey = isUuid(normalizedKey);

		// Skip UUID duplicate if we already have a non‑UUID alias stored
		if (uuidKey && Object.prototype.hasOwnProperty.call(result, normalizedKey)) return;

		// Prefer non‑UUID or first occurrence
		if (!uuidKey || !Object.prototype.hasOwnProperty.call(result, normalizedKey)) {
			result[normalizedKey] = value ?? null;
		}
	});

	// Second pass – remove UUID keys when an alias is present
	Object.keys(result).forEach((key) => {
		if (!isUuid(key)) return;
		const nonUuidAlias = normalizeKey(key);
		if (nonUuidAlias !== key && Object.prototype.hasOwnProperty.call(result, nonUuidAlias)) {
			delete result[key];
		}
	});

	return result;
};

// Dynamiczna lista kluczy rozpoznawanych jako functionalId: bierzemy wszystko, co nie jest UUID i ma długość <= 40 znaków.
const isFunctionalLike = (key: string) => {
	if (isUuid(key)) return false;
	return key.length > 0 && key.length <= 40;
};

/**
 * Rewrite catalog UUID attribute keys to functional ids from product/variant definitions.
 * Ensures print module fields survive preferUuidConfiguration and stay readable in admin.
 */
export const remapConfigurationKeysToFunctionalIds = (
	configuration: Record<string, string | number | null | undefined>,
	definitions: Array<ProductDefinition | null | undefined> = []
): Record<string, string | number | null> => {
	const uuidToFunctional: Record<string, string> = {};

	definitions.forEach((definition) => {
		if (!definition?.attributes) return;
		Object.values(definition.attributes).forEach((attr) => {
			const functionalId = String(attr.functionalId || attr.id || '');
			if (!functionalId || isUuid(functionalId)) return;
			[attr.id, attr.originalId].forEach((candidate) => {
				if (candidate && isUuid(String(candidate))) {
					uuidToFunctional[String(candidate)] = functionalId;
				}
			});
		});
	});

	if (!Object.keys(uuidToFunctional).length) {
		return Object.fromEntries(
			Object.entries(configuration ?? {}).map(([key, value]) => [key, value ?? null])
		);
	}

	const result: Record<string, string | number | null> = {};
	Object.entries(configuration ?? {}).forEach(([key, value]) => {
		const targetKey = uuidToFunctional[key] || key;
		if (result[targetKey] !== undefined && isUuid(key)) return;
		result[targetKey] = value ?? null;
	});
	return result;
};

/**
 * Strip functional keys if any UUID keys are present in payload, keeping UUIDs for backend.
 * Preserved keys (quantity, size, print.*, …) are kept even alongside UUID attributes.
 */
export const preferUuidConfiguration = (
	configuration: Record<string, string | number | null | undefined>
): Record<string, string | number | null> => {
	const hasUuid = Object.keys(configuration ?? {}).some((key) => isUuid(key));
	if (!hasUuid) return canonicalizeConfiguration(configuration);

	const result: Record<string, string | number | null> = {};
	Object.entries(configuration ?? {}).forEach(([key, value]) => {
		if (
			!isUuid(key) &&
			isFunctionalLike(normalizeKey(key)) &&
			!isPreservedConfigurationKey(key)
		) {
			return; // jeśli są UUID, pomijamy functionalne (oprócz zachowanych kluczy)
		}
		result[key] = value ?? null;
	});

	// Drop catalog UUID duplicates when a preserved functional alias carries the same field.
	Object.keys(result).forEach((key) => {
		if (!isUuid(key)) return;
		const duplicateFunctional = Object.keys(result).find(
			(candidate) =>
				!isUuid(candidate) &&
				isPreservedConfigurationKey(candidate) &&
				result[candidate] === result[key]
		);
		if (duplicateFunctional) {
			delete result[key];
		}
	});

	return result;
};

export default canonicalizeConfiguration;
